import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './AdminTable.css';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('all');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user, navigate, currentPage, role]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, per_page: 20 };
      if (search) params.search = search;
      if (role !== 'all') params.role = role;

      const response = await api.get('/api/admin/users', { params });
      setUsers(response.data.users || []);
      setPagination(response.data.pagination);
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.users.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const toggleHost = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/toggle-host`);
      toast.success(t('admin.users.hostStatusUpdated'));
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.users.failedToUpdate'));
    }
  };

  const toggleAdmin = async (userId) => {
    try {
      await api.post(`/api/admin/users/${userId}/toggle-admin`);
      toast.success(t('admin.users.adminStatusUpdated'));
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.users.failedToUpdate'));
    }
  };

  const toggleVerified = async (userId, currentStatus) => {
    try {
      await api.put(`/api/admin/users/${userId}`, { is_verified: !currentStatus });
      toast.success(t('admin.users.verificationStatusUpdated'));
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.users.failedToUpdate'));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>{t('admin.users.title')}</h1>
          <p>{t('admin.users.subtitle')}</p>
        </div>
      </div>

      <div className="filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder={t('admin.users.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit">{t('common.search')}</button>
        </form>

        <select value={role} onChange={(e) => { setRole(e.target.value); setCurrentPage(1); }}>
          <option value="all">{t('admin.users.allUsers')}</option>
          <option value="host">{t('admin.users.hostsOnly')}</option>
          <option value="admin">{t('admin.users.adminsOnly')}</option>
          <option value="guest">{t('admin.users.guestsOnly')}</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">{t('admin.users.loading')}</div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{t('admin.users.user')}</th>
                  <th>{t('admin.users.email')}</th>
                  <th>{t('admin.users.roles')}</th>
                  <th>{t('admin.users.stats')}</th>
                  <th>{t('admin.users.joined')}</th>
                  <th>{t('admin.users.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="user-cell">
                        <div className="user-name">{u.first_name} {u.last_name}</div>
                        {u.is_verified && <span className="badge-verified">✓ {t('admin.users.verified')}</span>}
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <div className="role-badges">
                        {u.is_admin && <span className="role-badge admin">{t('admin.users.admin')}</span>}
                        {u.is_host && <span className="role-badge host">{t('admin.users.host')}</span>}
                        {!u.is_host && !u.is_admin && <span className="role-badge guest">{t('admin.users.guest')}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="stats-cell">
                        {u.stats.properties > 0 && <span>🏠 {u.stats.properties}</span>}
                        {u.stats.bookings > 0 && <span>📅 {u.stats.bookings}</span>}
                      </div>
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => toggleHost(u.id)}
                          className="btn-sm"
                          title={u.is_host ? t('admin.users.removeHost') : t('admin.users.makeHost')}
                        >
                          {u.is_host ? '🏠-' : '🏠+'}
                        </button>
                        <button
                          onClick={() => toggleAdmin(u.id)}
                          className="btn-sm"
                          title={u.is_admin ? t('admin.users.removeAdmin') : t('admin.users.makeAdmin')}
                        >
                          {u.is_admin ? '⚙️-' : '⚙️+'}
                        </button>
                        <button
                          onClick={() => toggleVerified(u.id, u.is_verified)}
                          className="btn-sm"
                          title={u.is_verified ? t('admin.users.unverify') : t('admin.users.verify')}
                        >
                          {u.is_verified ? '✓-' : '✓+'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && (
              <div className="no-results">{t('admin.users.noUsers')}</div>
            )}
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="pagination">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                {t('common.previous')}
              </button>
              <span>{t('property.page')} {pagination.page} {t('property.of')} {pagination.pages}</span>
              <button
                disabled={currentPage === pagination.pages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminUsers;
